import { BulkConfirmationSummary } from '../models';

export interface BulkImportLabels {
  singular: string;
  plural: string;
}

export interface BulkImportCounts {
  uploaded: number;
  saved: number;
  duplicates?: number;
  confirmations?: BulkConfirmationSummary;
}

const DEFAULT_LABELS: BulkImportLabels = {
  singular: 'reservation',
  plural: 'reservations',
};

function listSkipReasons({ unsubscribed, noPhone, disabled }: BulkConfirmationSummary): string[] {
  return [
    unsubscribed > 0 ? `${unsubscribed} unsubscribed` : '',
    noPhone > 0 ? `${noPhone} without a phone` : '',
    disabled > 0 ? `${disabled} with texts turned off` : '',
  ].filter(Boolean);
}

function describeConfirmations(summary: BulkConfirmationSummary): string {
  const { queued, unsubscribed, noPhone, disabled } = summary;
  const skipped = unsubscribed + noPhone + disabled;
  if (queued === 0 && skipped > 0 && skipped === disabled) {
    return 'Confirmation texts are turned off in Settings, none were queued.';
  }
  if (skipped === 0) {
    return `Confirmation texts: ${queued} queued.`;
  }

  return `Confirmation texts: ${queued} queued, ${skipped} skipped (${listSkipReasons(summary).join(', ')}).`;
}

/**
 * One-sentence-per-fact summary of a bulk import for the manager's alert bar, so a
 * re-submitted file reads "0 saved, N duplicates" instead of a bare "Success".
 */
export function describeBulkImportResult(counts: BulkImportCounts, labels: BulkImportLabels = DEFAULT_LABELS): string {
  const { uploaded, saved, duplicates = 0, confirmations } = counts;
  const sentences = [`Saved ${saved} of ${uploaded} ${uploaded === 1 ? labels.singular : labels.plural}.`];

  if (duplicates > 0) {
    const noun = duplicates === 1 ? 'duplicate' : 'duplicates';
    sentences.push(`Skipped ${duplicates} ${noun} already booked for the same phone and time.`);
  }

  if (confirmations) {
    sentences.push(describeConfirmations(confirmations));
  }

  return sentences.join(' ');
}
